// src/Components/Staff/StaffTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Form, Spinner, Table } from "react-bootstrap";
import { db } from "../firebase";
import {
  arrayUnion,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { createStaffAccount } from "../firebaseSecondaryAuth";

const STAFF_TYPES = [
  "security",
  "housekeeping",
  "hospitality",
  "plumber",
  "electrician",
];

export default function StaffTab({ community }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    phone: "",
    staffType: "security",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const communityRef = useMemo(
    () => doc(db, "communities", community.id),
    [community.id],
  );

  // Live list of staff (resolved from community.staff UID arrays)
  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      communityRef,
      async (snap) => {
        if (!snap.exists()) {
          setStaffUsers([]);
          setLoading(false);
          return;
        }
        const data = snap.data() || {};
        const staff = data.staff || {};
        const allUids = STAFF_TYPES.flatMap((t) => staff?.[t] || []);
        // fetch user docs in parallel
        const users = [];
        for (const uid of allUids) {
          try {
            const usnap = await getDoc(doc(db, "users", uid));
            if (usnap.exists()) users.push({ id: uid, ...usnap.data() });
          } catch {
            // ignore missing
          }
        }
        setStaffUsers(
          users.sort((a, b) =>
            (a.staffType || "").localeCompare(b.staffType || ""),
          ),
        );
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError(err?.message || "Failed to load staff.");
        setLoading(false);
      },
    );
    return () => unsub();
  }, [communityRef]);

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const createStaff = async (e) => {
    e.preventDefault();
    setError("");
    const { fullName, email, password, phone, staffType } = form;
    if (!fullName || !email || !password || !staffType) {
      setError("Please fill name, email, password and staff type.");
      return;
    }
    if (!STAFF_TYPES.includes(staffType)) {
      setError("Invalid staff type.");
      return;
    }
    try {
      setSaving(true);

      // 1) Create Firebase Auth user in secondary app (doesn't disturb manager session)
      const authUser = await createStaffAccount(email.trim(), password);

      // 2) Create user profile
      const userDoc = doc(db, "users", authUser.uid);
      await setDoc(
        userDoc,
        {
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone?.trim() || "",
          role: staffType, // role is the staff type
          staffType,
          communityId: community.id,
          createdAt: serverTimestamp(),
        },
        { merge: true },
      );

      // 3) Add UID to community.staff[staffType] array
      await updateDoc(communityRef, {
        [`staff.${staffType}`]: arrayUnion(authUser.uid),
      });

      // Clear form
      setForm({ fullName: "", email: "", password: "", phone: "", staffType });
    } catch (err) {
      console.error("Create staff failed:", err);
      setError(err?.message || "Failed to create staff.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>Staff</strong>
        <small className="text-muted">
          Manager can add & manage staff for this community
        </small>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="warning">{error}</Alert>}

        <Form onSubmit={createStaff} className="mb-3">
          <div className="row g-2">
            <div className="col-12 col-md-3">
              <Form.Label className="fw-semibold">Full Name</Form.Label>
              <Form.Control
                name="fullName"
                value={form.fullName}
                onChange={onChange}
                placeholder="John Doe"
              />
            </div>
            <div className="col-12 col-md-3">
              <Form.Label className="fw-semibold">Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder="john@company.com"
              />
            </div>
            <div className="col-12 col-md-3">
              <Form.Label className="fw-semibold">Password</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={form.password}
                onChange={onChange}
                placeholder="Set initial password"
              />
              <Form.Text>Do not reuse; user can change later.</Form.Text>
            </div>
            <div className="col-12 col-md-3">
              <Form.Label className="fw-semibold">Mobile</Form.Label>
              <Form.Control
                name="phone"
                value={form.phone}
                onChange={onChange}
                placeholder="+91 9xxxxxxxxx"
              />
            </div>

            <div className="col-12 col-md-3">
              <Form.Label className="fw-semibold">Staff Type</Form.Label>
              <Form.Select
                name="staffType"
                value={form.staffType}
                onChange={onChange}
              >
                {STAFF_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="col-12 col-md-3 d-flex align-items-end">
              <Button type="submit" disabled={saving}>
                {saving ? "Creating…" : "Add Staff"}
              </Button>
            </div>
          </div>
        </Form>

        <hr />
        <h6 className="mb-2">Existing Staff</h6>
        {loading ? (
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>Loading…</span>
          </div>
        ) : (
          <Table responsive bordered hover size="sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Type</th>
                <th>UID</th>
              </tr>
            </thead>
            <tbody>
              {staffUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center">
                    No staff added
                  </td>
                </tr>
              ) : (
                staffUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{u.fullName || "—"}</td>
                    <td>{u.email || "—"}</td>
                    <td>{u.phone || "—"}</td>
                    <td>
                      <span className="badge bg-secondary text-capitalize">
                        {u.staffType || u.role}
                      </span>
                    </td>
                    <td>
                      <code>{u.id}</code>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
